import { ChevronRight } from "lucide-react";
import { forwardRef, type ReactNode } from "react";

import { cn } from "@/lib/utils";

interface ExpandableSectionHeaderProps {
  label: string;
  icon: ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  isActive?: boolean;
  /** When set, clicking the label/icon navigates instead of toggling. Chevron still toggles. */
  onNavigate?: () => void;
  sticky?: boolean;
  /** CSS top offset when sticky (e.g. to stack under another sticky header). */
  stickyTopPx?: number;
  stickyZIndex?: number;
  dropRef?: (node: HTMLElement | null) => void;
  isDropActive?: boolean;
  trailingAction?: ReactNode;
}

export const ExpandableSectionHeader = forwardRef<HTMLDivElement, ExpandableSectionHeaderProps>(
  (
    {
      label,
      icon,
      isExpanded,
      onToggle,
      isActive = false,
      onNavigate,
      sticky = false,
      stickyTopPx = 0,
      stickyZIndex = 10,
      dropRef,
      isDropActive = false,
      trailingAction,
    },
    ref,
  ) => {
    const handleRowClick = () => {
      if (onNavigate) {
        onNavigate();
        return;
      }
      onToggle();
    };

    return (
      <div
        ref={ref}
        className={cn("group/section relative", sticky && "sticky bg-white")}
        style={sticky ? { top: stickyTopPx, zIndex: stickyZIndex } : undefined}
      >
        <div
          ref={dropRef}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          className={cn(
            "relative flex cursor-pointer select-none items-center rounded-md px-2 py-1.5 text-sm transition-all duration-200",
            trailingAction && "pr-7",
            isActive
              ? "bg-gray-100 font-medium text-gray-900"
              : "text-gray-700 hover:bg-gray-50 hover:text-gray-900",
            isDropActive && "bg-blue-50 ring-1 ring-blue-200",
          )}
          style={{ paddingLeft: "8px" }}
          onClick={handleRowClick}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleRowClick();
            }
          }}
        >
          <div className="relative flex min-w-0 flex-1 items-center">
            <span
              role="presentation"
              aria-hidden="true"
              className={cn(
                "pointer-events-none absolute left-0 z-10 flex h-4 w-4 items-center justify-center",
                "opacity-0 transition-opacity duration-150",
                "group-hover/section:pointer-events-auto group-hover/section:opacity-100",
                "group-focus-within/section:pointer-events-auto group-focus-within/section:opacity-100",
              )}
              onClick={e => {
                e.stopPropagation();
                onToggle();
              }}
            >
              <ChevronRight
                className={cn(
                  "h-4 w-4 text-black transition-transform duration-200",
                  isExpanded && "rotate-90",
                )}
                strokeWidth={2}
              />
            </span>
            <div
              className={cn(
                "flex min-w-0 flex-1 items-center gap-1.5 transition-transform duration-150",
                "group-hover/section:translate-x-[18px]",
                "group-focus-within/section:translate-x-[18px]",
              )}
            >
              <span
                className={cn(
                  "flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center",
                  isActive || isDropActive ? "text-blue-500" : "text-gray-500",
                )}
              >
                {icon}
              </span>
              <span className={cn("flex-1 text-[13px]", isActive && "font-semibold")}>{label}</span>
            </div>
          </div>
        </div>
        {trailingAction ? (
          <div className="absolute right-1 top-1/2 z-20 -translate-y-1/2">{trailingAction}</div>
        ) : null}
      </div>
    );
  },
);

ExpandableSectionHeader.displayName = "ExpandableSectionHeader";
